import { useState } from 'react';
import axios from 'axios';

// Resultado normalizado para uso no formulário do cliente
export interface DadosRFB {
  cnpj: string;                  // Formatado (12.345.678/0001-90)
  razao_social: string;
  nome_fantasia: string;
  porte: string;
  cnae_codigo: string;
  cnae_descricao: string;
  endereco: string;              // Logradouro + número + complemento
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  contato_fone: string;
  contato_email: string;
  situacao_rfb: string;
  data_abertura: string | null;  // YYYY-MM-DD
}

export function formatarCnpj(cnpj: string): string {
  const d = String(cnpj || '').replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

// Consulta a BrasilAPI direto do navegador (CORS habilitado).
// Usamos isso porque a Lambda em VPC não tem saída para internet.
export async function consultarCnpjBrasilApi(cnpjRaw: string): Promise<DadosRFB> {
  const cnpj = String(cnpjRaw || '').replace(/\D/g, '');
  if (cnpj.length !== 14) throw new Error('CNPJ deve ter 14 dígitos');
  const res = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`, { timeout: 15000 });
  const d = res.data;
  const enderecoCompleto = [d.logradouro, d.numero, d.complemento]
    .filter(Boolean).join(', ');
  return {
    cnpj: formatarCnpj(d.cnpj || cnpj),
    razao_social: d.razao_social || '',
    nome_fantasia: d.nome_fantasia || '',
    porte: d.porte || d.descricao_porte || '',
    cnae_codigo: String(d.cnae_fiscal || ''),
    cnae_descricao: d.cnae_fiscal_descricao || '',
    endereco: enderecoCompleto,
    bairro: d.bairro || '',
    cidade: d.municipio || '',
    uf: d.uf || '',
    cep: d.cep || '',
    contato_fone: d.ddd_telefone_1 || '',
    contato_email: d.email || '',
    situacao_rfb: d.descricao_situacao_cadastral || '',
    data_abertura: d.data_inicio_atividade || null,
  };
}

// Componente reutilizável: caixa de busca CNPJ + botão. Recebe callback com os dados.
export default function BuscaCnpjBox({ cnpjInicial = '', onEncontrado, compacto = false }: {
  cnpjInicial?: string;
  onEncontrado: (dados: DadosRFB) => void;
  compacto?: boolean;
}) {
  const [cnpj, setCnpj] = useState(cnpjInicial);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState(false);

  async function buscar() {
    const limpo = cnpj.replace(/\D/g, '');
    if (limpo.length !== 14) { setErro('CNPJ deve ter 14 dígitos'); setOk(false); return; }
    setBuscando(true); setErro(''); setOk(false);
    try {
      const dados = await consultarCnpjBrasilApi(limpo);
      onEncontrado(dados);
      setOk(true);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 404) setErro('CNPJ não encontrado na base da Receita');
      else if (status === 429) setErro('Muitas consultas — aguarde alguns segundos e tente novamente');
      else setErro(e?.message || 'Erro ao consultar CNPJ');
    } finally { setBuscando(false); }
  }

  return (
    <div style={{
      padding: compacto ? 8 : 10, marginBottom: 12, background: '#FAFBFF',
      border: '0.5px solid #CDD4EA', borderRadius: 6,
    }}>
      {!compacto && (
        <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 6 }}>
          🔍 Buscar dados na Receita Federal (BrasilAPI)
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={cnpj} autoFocus
          onChange={e => { setCnpj(e.target.value); setErro(''); setOk(false); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscar(); } }}
          placeholder="CNPJ (com ou sem pontuação)"
          style={{ flex: 1, padding: '7px 10px', fontSize: 12, borderRadius: 5, border: '1px solid #ddd', boxSizing: 'border-box' }} />
        <button type="button" onClick={buscar} disabled={buscando}
          style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600,
            border: 'none', borderRadius: 5, background: '#0930D0', color: '#fff',
            cursor: buscando ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
          {buscando ? 'Buscando…' : '🔍 Buscar'}
        </button>
      </div>
      {erro && <div style={{ fontSize: 10, color: '#B91626', marginTop: 4 }}>{erro}</div>}
      {ok && <div style={{ fontSize: 10, color: '#0a7f4a', marginTop: 4, fontWeight: 500 }}>
        ✓ Dados encontrados — campos preenchidos abaixo.
      </div>}
    </div>
  );
}
